import React from 'react';
import { Link } from 'react-router-dom';
import LegalPolicyLayout from '@/components/legal/LegalPolicyLayout';
import { LEGAL_ENTITY, LEGAL_ROUTES } from '@/constants/legalEntity';

export const TermsAndConditions: React.FC = () => {
  return (
    <LegalPolicyLayout title="Terms of Service">
      <section className="mb-8">
        <h2>1. Agreement</h2>
        <p>
          These Terms of Service (“Terms”) govern access to {LEGAL_ENTITY.productName} (“Service”), a software
          platform operated by {LEGAL_ENTITY.legalNameEn} ({LEGAL_ENTITY.legalNameMk}), registered in{' '}
          {LEGAL_ENTITY.country}. By using the Service, you agree to these Terms, our{' '}
          <Link to={LEGAL_ROUTES.privacy}>Privacy Policy</Link>,{' '}
          <Link to={LEGAL_ROUTES.refund}>Refund Policy</Link>, and{' '}
          <Link to={LEGAL_ROUTES.cookies}>Cookie Policy</Link>.
        </p>
      </section>

      <section className="mb-8">
        <h2>2. The Service</h2>
        <p>
          {LEGAL_ENTITY.productName} helps music teachers and schools manage students, bookings, lesson
          packages, portfolios, blogs, and related tools. The Service includes optional AI-assisted practice
          features for students. We may update features from time to time.
        </p>
      </section>

      <section className="mb-8">
        <h2>3. Accounts — invite-only access</h2>
        <p>
          There is <strong>no public self-registration</strong> for teachers or students on the marketing
          website.
        </p>
        <ul>
          <li>
            <strong>Teachers</strong> — Studio accounts are created by {LEGAL_ENTITY.legalNameEn} or an
            authorized administrator after onboarding. Teachers receive credentials or a secure link to
            access the dashboard.
          </li>
          <li>
            <strong>Students</strong> — Accounts are created when a teacher invites a student (email or
            invite link). Students accept the invitation and sign in to book lessons, pay for packages, or use
            AI features where enabled.
          </li>
        </ul>
        <p>
          You must provide accurate information and keep your password confidential. You are responsible for
          activity under your account.
        </p>
      </section>

      <section className="mb-8">
        <h2>4. Payments</h2>
        <p>
          Paid features are billed through <strong>Lemon Squeezy</strong> (merchant of record for applicable
          transactions). By purchasing, you also agree to Lemon Squeezy’s buyer terms where they apply.
        </p>
        <ul>
          <li>
            <strong>Teachers</strong> — Platform use is primarily <strong>pay-as-you-go</strong>: a platform
            fee applies to student payments processed through the Service (see{' '}
            <Link to="/pricing">Pricing</Link>).
          </li>
          <li>
            <strong>Students</strong> — Pay teachers for <strong>lesson subscriptions</strong> or packages at
            prices set by each teacher, and may purchase <strong>one-time AI practice</strong> features where
            offered.
          </li>
        </ul>
        <p>
          Refunds are described in our <Link to={LEGAL_ROUTES.refund}>Refund Policy</Link> (including the
          14-day full refund for unused student subscriptions and AI purchases).
        </p>
      </section>

      <section className="mb-8">
        <h2>5. Teacher responsibilities</h2>
        <p>
          Teachers are independent providers of music lessons. They set their own prices, schedules, and
          policies for students. {LEGAL_ENTITY.legalNameEn} provides software and payment facilitation but does
          not employ teachers or guarantee lesson quality.
        </p>
      </section>

      <section className="mb-8">
        <h2>6. Acceptable use</h2>
        <p>You agree not to misuse the Service, including: illegal content, harassment, fraud, scraping, or
          attempting to bypass security or billing.</p>
      </section>

      <section className="mb-8">
        <h2>7. Intellectual property</h2>
        <p>
          The Service, branding, and software are owned by {LEGAL_ENTITY.legalNameEn} or its licensors.
          Teachers retain rights to their own content (e.g. blog posts, portfolio text) and grant us a license
          to host and display it as needed to operate the Service.
        </p>
      </section>

      <section className="mb-8">
        <h2>8. Availability and changes</h2>
        <p>
          We strive for reliable uptime but do not guarantee uninterrupted access. We may modify or discontinue
          features with reasonable notice where practicable.
        </p>
      </section>

      <section className="mb-8">
        <h2>9. Limitation of liability</h2>
        <p>
          To the maximum extent permitted by law, {LEGAL_ENTITY.legalNameEn} is not liable for indirect,
          incidental, or consequential damages, or for amounts exceeding fees you paid us in the twelve months
          before the claim. Some jurisdictions do not allow certain limits; your statutory rights remain.
        </p>
      </section>

      <section className="mb-8">
        <h2>10. Governing law</h2>
        <p>
          These Terms are governed by the laws of {LEGAL_ENTITY.country}, without regard to conflict-of-law
          rules. Courts in North Macedonia shall have exclusive jurisdiction unless mandatory consumer law in
          your country requires otherwise.
        </p>
      </section>

      <section className="mb-8">
        <h2>11. Contact</h2>
        <p>
          {LEGAL_ENTITY.legalNameEn} · {LEGAL_ENTITY.contactEmail} · {LEGAL_ENTITY.productName}
        </p>
      </section>
    </LegalPolicyLayout>
  );
};
